import { useRouter } from 'next/router';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAboutYourself } from '../../../features/registrationSlice';
import { RootState } from '../../../store/store';
import AboutQuestion from './AboutQuestion';
import RegWorkflowFactory from '../RegWorkflowFactory';
import { RegWorkflowContainer } from '../RegistrationWorkflow.style';
import { setScrollId } from '../../../features/scrollIdSlice';
import { getQuery } from '../../../lib/utils/common';
import { regExpFromSitecorePattern } from 'src/lib/utils/contactForm';

const AboutYourself = (props: any): JSX.Element => {
  const router = useRouter();
  const { query } = router;
  const dispatch = useDispatch();
  const storedAnswers = useSelector((state: RootState) => state.registrationDetails.aboutYourSelf);

  const [enableNext, setEnableNext] = useState<boolean>(false);
  const [regexError, setRegexError] = useState<any>({ firstName: false, lastName: false });
  const firstNameRegexPattern = useCallback(() => {
    return regExpFromSitecorePattern(props?.fields?.firstNameRegex?.fields?.value?.value);
  }, [props?.fields?.firstNameRegex?.fields?.value?.value]);
  const lastNameRegexPattern = useCallback(() => {
    return regExpFromSitecorePattern(props?.fields?.lastNameRegex?.fields?.value?.value);
  }, [props?.fields?.lastNameRegex?.fields?.value?.value]);

  useEffect(() => {
    const isFirstNameRequired = props?.fields?.isFirstNameRequired.value;
    const isLastNameRequired = props?.fields?.isLastNameRequired.value;
    const currentFirstName = storedAnswers.firstName;
    const currentLastName = storedAnswers.lastName;
    const isAnyRequiredFieldEmpty =
      (isFirstNameRequired && currentFirstName.trim() === '') ||
      (isLastNameRequired && currentLastName.trim() === '');
    const isAnyRegexInvalid = regexError.firstName || regexError.lastName;
    const isFirstNameInvalidFormat =
      currentFirstName !== '' && !firstNameRegexPattern().test(currentFirstName);
    const isLastNameInvalidFormat =
      currentLastName !== '' && !lastNameRegexPattern().test(currentLastName);
    const isAnyFieldInvalidFormat = isFirstNameInvalidFormat || isLastNameInvalidFormat;
    setEnableNext(!isAnyRequiredFieldEmpty && !isAnyRegexInvalid && !isAnyFieldInvalidFormat);
  }, [
    storedAnswers,
    regexError,
    props?.fields?.isFirstNameRequired?.value,
    props?.fields?.isLastNameRequired?.value,
    firstNameRegexPattern,
    lastNameRegexPattern,
  ]);
  const validateInputs = () => {
    let isValid = true;
    const firstNameRegex = firstNameRegexPattern();
    const lastNameRegex = lastNameRegexPattern();
    if (props?.fields?.isFirstNameRequired.value && storedAnswers.firstName.trim() === '') {
      isValid = false;
    } else if (
      storedAnswers.firstName.trim() !== '' &&
      !firstNameRegex.test(storedAnswers.firstName)
    ) {
      setRegexError((prev) => ({ ...prev, firstName: true }));
      isValid = false;
    } else {
      setRegexError((prev) => ({ ...prev, firstName: false }));
    }
    if (props?.fields?.isLastNameRequired.value && storedAnswers.lastName.trim() === '') {
      isValid = false;
    } else if (
      storedAnswers.lastName.trim() !== '' &&
      !lastNameRegex.test(storedAnswers.lastName)
    ) {
      setRegexError((prev) => ({ ...prev, lastName: true }));
      isValid = false;
    } else {
      setRegexError((prev) => ({ ...prev, lastName: false }));
    }

    return isValid;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, name: string) => {
    const newValue = event.target.value;
    dispatch(setAboutYourself({ [name]: newValue }));

    const firstNameRegex = firstNameRegexPattern();
    const lastNameRegex = lastNameRegexPattern();
    let currentFirstNameError = regexError.firstName;
    let currentLastNameError = regexError.lastName;
    if (name === 'firstName') {
      if (newValue.trim() !== '' && !firstNameRegex.test(newValue)) {
        currentFirstNameError = true;
      } else {
        currentFirstNameError = false;
      }
    } else if (name === 'lastName') {
      if (newValue.trim() !== '' && !lastNameRegex.test(newValue)) {
        currentLastNameError = true;
      } else {
        currentLastNameError = false;
      }
    }
    if (
      currentFirstNameError !== regexError.firstName ||
      currentLastNameError !== regexError.lastName
    ) {
      setRegexError({
        firstName: currentFirstNameError,
        lastName: currentLastNameError,
      });
    }
  };

  const handleClick = (event: any, url: any, action: any) => {
    event.preventDefault();
    if (action === 'back') {
      if (url !== '') {
        dispatch(setScrollId('getstarted'));
        router.push(
          {
            pathname: url,
            query: getQuery(query),
          },
          url
        );
      } else {
        history.back();
      }
    } else {
      const isValid = validateInputs();
      if (isValid) {
        router.push(
          {
            pathname: url,
            query: getQuery(query),
          },
          url
        );
      }
    }
  };
  if (!props?.fields) return <></>;

  return (
    <RegWorkflowContainer id="about-yourself" className="ui-about-yourself">
      <RegWorkflowFactory
        title={props?.fields?.title}
        currentPage={1}
        totalCount={5}
        buttonsItems={props?.fields?.ctaLinks}
        handleClick={handleClick}
        enableNext={enableNext}
        className="ui-about-yourself__factory"
      >
        <AboutQuestion
          {...props.fields}
          handleChange={handleChange}
          regexError={regexError}
          storedAnswers={storedAnswers}
          className="ui-about-yourself__question"
        />
      </RegWorkflowFactory>
    </RegWorkflowContainer>
  );
};

export default AboutYourself;
